import {
  createMongoAbility,
  type MongoAbility,
  type MongoQuery,
  type SubjectRawRule,
} from '@casl/ability';
import { useMemo } from 'react';
import { useAgencySession } from './session.js';

export type Action = 'manage' | 'create' | 'read' | 'update' | 'delete' | 'transition';
export type Subject =
  | 'Zone'
  | 'PropertyTypology'
  | 'AppointmentType'
  | 'Contact'
  | 'Building'
  | 'Property'
  | 'Mandate'
  | 'Demand'
  | 'Activity'
  | 'User'
  | 'Agency'
  | 'Queue'
  // The connection and the threads are separate subjects (M8): connecting the
  // agency's number is a manager's act, answering customers is everyone's.
  | 'WhatsAppAccount'
  | 'Conversation'
  // A conversation with the data assistant (M15). Owned by one person inside
  // the agency, same as `Conversation` — the ability grants the feature, the
  // API's own `user_id` predicate grants the row.
  | 'AssistantSession'
  // What the assistant costs the agency, and the ceiling it is checked
  // against (M15.3). Separate from the conversation on purpose: asking a
  // question and reading the bill are not the same permission.
  | 'AssistantUsage'
  // Segments, message templates and campaigns (M9 / P2). One subject for the
  // three: whoever may launch a campaign may write the message it sends.
  | 'Campaign'
  | 'all';
export type AppAbility = MongoAbility<[Action, Subject]>;

/**
 * The API's rules, rebuilt from the session response.
 *
 * This hides what the server would refuse — it does not enforce anything. Every
 * one of these checks is made again in the controller; if the two ever disagree
 * the API wins, and the UI is the side with the bug.
 */
export function useAbility(): AppAbility {
  const session = useAgencySession();

  return useMemo(
    () =>
      createMongoAbility<AppAbility>(
        session.abilities as SubjectRawRule<Action, Subject, MongoQuery>[],
      ),
    [session.abilities],
  );
}

/** Permissions in the shape the resource kit expects. */
export function useResourcePermissions(subject: Subject) {
  const ability = useAbility();
  return {
    read: ability.can('read', subject),
    create: ability.can('create', subject),
    update: ability.can('update', subject),
    remove: ability.can('delete', subject),
    // Only mandates use this today; a resource without transitions simply gets
    // false, which is what its screens already assume.
    transition: ability.can('transition', subject),
  };
}
